<template>
  <div>
    <!-- Страницы входа рисуются во весь экран: и операторская, и клиентская. -->
    <v-app v-if="isLoginRoute">
      <keep-alive>
        <router-view/>
      </keep-alive>
    </v-app>

    <!-- Кабинет клиента живёт в своём каркасе: без меню оператора, смен и
         отчётов — там другой пользователь и другой набор страниц. -->
    <ClientShell v-else-if="isClientRoute"/>
    <!-- Класс режима меню нужен разметке: в режиме полосы рабочая область
         держит ширину по свёрнутому меню, и раскрытие её не двигает. -->
    <v-app v-else :class="isMobile ? 'nav--mobile' : `nav--${navMode}`">
      <NavPanel/>

      <!-- На телефоне в шапке остаются все значки, уходят только часы и имя;
           смена — второй строкой во всю ширину: в одну строку с ними всё это
           не помещалось. -->
      <v-app-bar
        app
        dark
        clipped-left
        flat
        height="52"
        :extended="isMobile"
        extension-height="44"
        class="sts-appbar"
      >
        <v-btn v-if="isMobile" icon @click="toggleDrawer()">
          <v-icon>mdi-menu</v-icon>
        </v-btn>
        <v-tooltip v-else bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="switchNavMode()">
              <v-icon>{{ navModeIcon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ navModeLabel }} — нажмите, чтобы сменить</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon @click="toggleTheme()" v-on="on">
              <v-icon>{{ theme === 'dark' ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ theme === 'dark' ? 'Светлая тема' : 'Тёмная тема' }}</span>
        </v-tooltip>
        <v-toolbar-title class="sts-brand">Lorus</v-toolbar-title>
        <work-shift-action v-if="!isMobile" ref="work_shift"/>
        <v-spacer/>
        <Clock v-if="!isMobile"/>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <ReportsMenu/>
            </span>
          </template>
          <span>Готовые отчёты</span>
        </v-tooltip>

        <!-- Обращения клиентов: счётчик показывает те, где ждут ответа
             терминала. Клиент пишет из кабинета, и без этого значка сообщение
             заметили бы только при заходе на страницу. -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="$router.push('/client-threads')">
              <v-badge :value="threadsUnread > 0" :content="threadsUnread" color="error" overlap>
                <v-icon>mdi-forum-outline</v-icon>
              </v-badge>
            </v-btn>
          </template>
          <span>Обращения клиентов{{ threadsUnread ? `: ждут ответа ${threadsUnread}` : '' }}</span>
        </v-tooltip>
        <!-- ФИО, если оператор его указал: почта в шапке ничего не говорила о
             том, кто именно работает. Сам адрес остался в подсказке. -->
        <v-tooltip v-if="!isMobile" bottom>
          <template v-slot:activator="{ on }">
            <div class="sts-user" v-on="on">{{ user.name || user.email }}</div>
          </template>
          <span>{{ user.email }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="profile_dialog = true">
              <v-icon>mdi-cog-outline</v-icon>
            </v-btn>
          </template>
          <span>Профиль — ФИО, пароль и устройства</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon @click="signOut()" v-on="on">
              <v-icon>mdi-logout</v-icon>
            </v-btn>
          </template>
          <span>Выход</span>
        </v-tooltip>

        <!-- Телефон: под значками — смена во всю ширину. -->
        <template v-if="isMobile" v-slot:extension>
          <work-shift-action ref="work_shift" compact/>
        </template>
      </v-app-bar>

      <!-- Телефон: время и кто работает — в подвале, чтобы не растить шапку. -->
      <v-footer v-if="isMobile" app dark class="sts-mobile-footer">
        <Clock/>
        <span class="sts-user sts-user--mobile" :title="user.email">{{ user.name || user.email }}</span>
      </v-footer>

      <v-main app>
        <BlockContent/>
      </v-main>
      <report-toast />
      <!-- Профиль: ФИО и пароль, ниже — с каких адресов открыт доступ и
           кнопка выйти на остальных устройствах. -->
      <ProfileDialog v-model="profile_dialog"/>
    </v-app>

    <!-- Просмотр снимков общий для всего приложения: фотографии открывают и
         с обычной страницы, и из диалога, и в кабинете клиента. -->
    <ImageViewer/>
  </div>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import terminalChannel from '@/mixins/terminalChannel'
import { preferredTheme, applyTheme } from '@/shared/lib/theme'
import { cableUrl } from '@/shared/lib/apiBase'
import { nextNavMode, NAV_MODE_ICONS, NAV_MODE_LABELS } from '@/shared/lib/navMode'

// Обёртка рабочей области — без неё страницы нет вообще, а лениво она лишь
// удлиняла цепочку чанков перед первым кадром. Сами страницы внутри router-view
// остаются ленивыми.
import BlockContent from "./components/BlockContent.vue";
// Каркас личного кабинета: своя шапка и свои разделы.
const ClientShell = () => import("./components/Client/Shell.vue");
// Меню — своя часть шасси со своим списком пунктов и правилами показа.
import NavPanel from "./components/NavPanel.vue";
const ReportsMenu = () => import('./components/Helpers/ReportsMenu.vue');
const ReportToast = () => import("./components/Helpers/ReportToast.vue");
const WorkShiftAction = () => import("./components/WorkShift/Action");
const Clock = () => import("./components/Clock.vue");
const ProfileDialog = () => import("./components/Profile/ProfileDialog.vue");
// Окно просмотра снимков: пока ни одна фотография не открыта, рисует пустоту.
const ImageViewer = () => import("./components/Helpers/ImageViewer.vue");

export default {
  name: 'App',
  mixins: [terminalChannel],
  components: {
    ReportsMenu,
    BlockContent,
    ClientShell,
    NavPanel,
    Clock,
    ReportToast,
    ProfileDialog,
    ImageViewer,
    WorkShiftAction
  },
  data () {
    return {
      theme: preferredTheme(),
      profile_dialog: false,
      threadsUnread: 0,
      channel: null,
      cable_consumer: null,
      report_channel: null,
    }
  },
  watch: {
    // При логине App.vue уже смонтирован — всё, что делается «после входа»,
    // поднимаем через watch на токен. К этому моменту loginAction уже вызвал
    // connection.connect(), _cable установлен.
    '$store.state.token'(newToken) {
      if (!newToken) {
        // Вышли — следующий вход снова должен всё загрузить.
        this._sessionStarted = false
        return
      }

      if (this.isClientRoute) return

      this.$store.dispatch('cable/flushPending')
      this._subscribeGlobalChannels()
      this.startSession()
    },
  },
  mounted() {
    this.$store.dispatch('cable/init', this.$cable)

    // В кабинете операторские данные не грузим, даже если в этом браузере
    // осталась операторская кука: её 401 выбрасывал клиента на /login.
    if (this.isClientRoute || this.$route.path === '/client/login') return

    if (this.$cookies.get('token')) {
      const token = this.$cookies.get('token')
      // При обновлении страницы connectImmediately: false → _cable === null.
      // Явно вызываем connect, чтобы _cable создался до подписок.
      this.$cable.connection.connect(cableUrl(token))
      this._subscribeGlobalChannels()
      this.startSession()
    }
  },
  beforeDestroy() {
    this.$root.$off('operator-threads-refresh', this.fetchThreadsUnread)
    this.$store.dispatch('cable/unsubscribeAll')
  },
  computed: {
    ...mapState(['user', 'alert', 'work_shift', 'companies']),
    isLoginRoute () {
      return this.$route.path === '/login' || this.$route.path === '/client/login'
    },
    isClientRoute () {
      return this.$route.path.startsWith('/client/') || this.$route.path === '/client'
    },
    navMode () {
      return this.$store.state.nav_mode
    },
    // Телефон и планшет в портрете: меню выезжает по кнопке, шапка сжата.
    isMobile () {
      return this.$vuetify.breakpoint.smAndDown
    },
    navModeIcon () {
      return NAV_MODE_ICONS[this.navMode]
    },
    navModeLabel () {
      return NAV_MODE_LABELS[this.navMode]
    },
    work_shift_connect() {
      return this.$store.getters['cable/isChannelConnected']('WorkShiftChannel')
    },
    reports_connect() {
      return this.$store.getters['cable/isChannelConnected']('ReportChannel')
    },
    company_id: {
      get () {
        return this.$store.state.company_id
      },
      set (value) {
        this.$store.state.company_id = value
      }
    }
  },
  methods: {
    ...mapActions(['current_user']),
    // Всё, что нужно оператору сразу после входа. Раньше это висело в mounted
    // под проверкой куки: при обновлении страницы токен уже есть и всё
    // грузилось, а при первом входе App.vue к тому моменту смонтирован —
    // токен появлялся позже, и ни пользователь, ни смена, ни компании не
    // запрашивались. Меню строится из пользователя и смены, поэтому и
    // оставалось пустым до перезагрузки.
    startSession() {
      if (this._sessionStarted) return

      this._sessionStarted = true

      this.getUser()
      this.getWorkShift()
      this.fetchCompanies()
      this.fetchThreadsUnread()

      // Сообщение из кабинета всплывает у оператора сразу, а не при заходе
      // на страницу обращений.
      this.watchTerminal(['client_message'], (event) => {
        this.fetchThreadsUnread()

        if (event && event.from_client) {
          this.$toast.info(`Новое сообщение в обращении: ${event.subject_name || event.thread_title || 'Общий вопрос'}`)
        }
      })

      this.$root.$on('operator-threads-refresh', this.fetchThreadsUnread)
      this.$root.$on('operator-threads-unread', (value) => { this.threadsUnread = value })
    },
    fetchThreadsUnread() {
      this.$http.get('/api/client_threads/unread_count')
        .then(response => { this.threadsUnread = response.data.unread })
        .catch(() => {})
    },
    toggleTheme() {
      this.theme = applyTheme(this.theme === 'dark' ? 'light' : 'dark', this.$vuetify)
    },
    _subscribeGlobalChannels() {
      this.$store.dispatch('cable/subscribe', {
        channel: 'WorkShiftChannel',
      })
      this.$store.dispatch('cable/subscribe', {
        channel: 'ReportChannel',
      })
    },
    switchNavMode() {
      this.$store.commit('update_nav_mode', nextNavMode(this.navMode))
    },
    toggleDrawer() {
      this.$store.commit('update_nav_drawer', !this.$store.state.nav_drawer)
    },
    async fetchCompanies() {
      this.$http.get('api/companies?page=1&sort_by=position')
        .then((response) => {
          this.$store.commit('set_companies', response.data.items)
          this.company_id = this.companies[0].id
        })
    },
    signOut () {
      console.log('logout')
      this.$store.dispatch('logout').then(() => { this.$router.push('/login') })
    },
    getUser() {
      this.current_user()
    },
    getWorkShift () {
      this.$http.get(`/api/work_shifts/active_work_shift`).then(response => {
        this.$store.commit('open_shift_success', response.data)
      }).catch((error) => {
        this.$store.commit('updateAlert', {
          active: true,
          message: "Смена ещё не начата, чтобы начать работу пожалуйта нажмите 'НАЧАТЬ СМЕНУ'",
          type: 'info'
        })
      })
    },
  }
}
</script>

<style>
/* На большом экране страница не прокручивается: списки держат свою высоту и
   скроллятся внутри себя, а окно остаётся на месте — так шапка и вкладки
   всегда на виду.
   На телефоне всё наоборот: таблица идёт карточками во всю длину (миксин
   tableHeight там высоту не задаёт), и без прокрутки документа до низа страницы
   было не добраться. */
html {
  overflow: hidden;
}

@media (max-width: 959px) {
  html,
  body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: #e6e6e6;
  border-left: 1px solid #dadada;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #b0b0b0;
  border: solid 2px #e6e6e6;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: black;
}

.cursor-pointer {
  cursor: pointer;
}

/* Название системы в верхней панели — сдержанное, оно не должно
   конкурировать с заголовком страницы. */
.sts-brand {
  font-size: 15px !important;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.sts-user {
  margin: 0 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.v-data-table.compact-table {
  table {
    tr {
      th, td {
        padding: 0 6px !important;
      }
    }
  }
}

.dashboard-container {
  max-height: calc(100vh - 64px);
  overflow: auto;
}

.style-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.style-around {
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
}
.style-center {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.set-company {
  width: 10vw;
}

.min-height-table {
  min-height: 67vh;
  height: 67vh;
  max-height: 67vh;
}

.vehicle-images {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-column-gap: 2px;
  grid-row-gap: 2px;
}
</style>
